// Einzige Wahrheit für die App-Navigation.
//
// Vorher gab es VIER unabhängige Zielkataloge — Sidebar.tsx (12 Ziele),
// MobileNav.tsx (5 Ziele, eigene Labels: "Aufgaben" statt "Meine Aufgaben"),
// CommandPalette.tsx (eigene Liste) und hotkeys.ts (eigene g-Chord-
// Zuordnung). Jeder pflegte Labels, Icons und Auswahl unabhängig, deshalb
// hiess dasselbe Ziel an drei Stellen anders und fehlte an der vierten
// (z. B. "Projekte" doppelt in Sidebar.tsx, "Inbox" auf iOS/Mobile aber
// "Posteingang" gedacht). Dieses Modul ersetzt alle vier Kataloge durch
// EINE Liste (NAV_TARGETS) — abgeleitete Sichten (primär, mobil, Fusszeile,
// Befehlspalette, Hotkeys) sind FUNKTIONEN darüber, keine kopierten Listen.
// Eine zweite Stelle, an der ein Ziel neu definiert werden könnte, gibt es
// damit nicht mehr — Label-/Icon-Drift wird strukturell unmöglich.
//
// Es wird KEIN Routen-Pfad umbenannt: Deep-Links und die iOS-App kennen die
// bestehenden Pfade.

import {
  CalendarClock,
  CheckSquare,
  Folder,
  Inbox,
  LayoutDashboard,
  Settings,
  Shield,
  Star,
  Sun,
  Target,
} from "lucide-preact";
import { uxEnabled } from "./ux";

/** Icon-Komponententyp, wie lucide-preact ihn exportiert. */
export type NavIcon = typeof Sun;

/**
 * "primary"   — Hauptziele der oberen/linken Navigation und der mobilen
 *               Bottom-Bar (max. 5).
 * "workspace" — Unter-Tabs bzw. Baum-Abschnitte; per Deep-Link weiter
 *               erreichbar, aber keine Hauptziele mehr.
 * "footer"    — Fussbereich (Einstellungen, Admin).
 */
export type NavGroup = "primary" | "workspace" | "footer";

export interface NavTarget {
  /** Stabiler Bezeichner, unabhängig von Label/Pfad. */
  id: string;
  /** Routen-Pfad. Bleibt stabil (Deep-Links, iOS-App). */
  path: string;
  /** Deutsches Anzeige-Label. Kommt über alle Ziele hinweg genau einmal vor. */
  label: string;
  icon: NavIcon;
  group: NavGroup;
  /** Gehört in die mobile Bottom-Bar. */
  mobile: boolean;
  /** Zweite Taste eines g-Chords (z. B. "h" für "g h"). */
  hotkey?: string;
  /** Alte g-Chords, die weiter funktionieren sollen (Muskelgedächtnis). */
  hotkeyAliases?: readonly string[];
  /** Bezeichner des Zählers, der an diesem Ziel hängt (z. B. "inbox"). */
  badge?: string;
  /** Nur für Admins sichtbar/erreichbar. */
  adminOnly?: boolean;
  /** Zusätzliche Suchbegriffe für die Befehlspalette, u. a. alte Namen. */
  paletteAliases?: string[];
  /** Bei "workspace"-Zielen: das primäre Ziel, dessen Tab dies ist. */
  parentId?: string;
}

export const NAV_TARGETS: readonly NavTarget[] = [
  // Hauptziele — in dieser Reihenfolge, auch für die mobile Bottom-Bar.
  {
    id: "today",
    path: "/today",
    label: "Mein Tag",
    icon: Sun,
    group: "primary",
    mobile: true,
    hotkey: "h",
    paletteAliases: ["heute", "my day", "tagesplanung"],
  },
  {
    id: "my-tasks",
    path: "/my-tasks",
    label: "Meine Aufgaben",
    icon: CheckSquare,
    group: "primary",
    mobile: true,
    hotkey: "a",
    hotkeyAliases: ["m"], // alter Chord `g m` (hotkeys.ts) bleibt funktionsfähig
    paletteAliases: ["aufgaben", "my tasks", "todo"],
  },
  {
    id: "projects",
    path: "/projects",
    label: "Projekte",
    icon: Folder,
    group: "primary",
    mobile: true,
    hotkey: "p",
  },
  {
    id: "inbox",
    path: "/inbox",
    label: "Inbox",
    icon: Inbox,
    group: "primary",
    mobile: true,
    hotkey: "i",
    badge: "inbox",
    paletteAliases: ["posteingang", "benachrichtigungen"],
  },
  {
    id: "dashboard",
    path: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    group: "primary",
    mobile: true,
    hotkey: "u",
    paletteAliases: ["übersicht", "uebersicht", "home", "start"],
  },

  // Workspace — Unter-Tabs/Baum-Abschnitte, weiter per Deep-Link erreichbar.
  {
    id: "favorites",
    path: "/favorites",
    label: "Favoriten",
    icon: Star,
    group: "workspace",
    mobile: false,
    paletteAliases: ["angeheftet", "gemerkt"],
  },
  {
    id: "goals",
    path: "/goals",
    label: "Ziele",
    icon: Target,
    group: "workspace",
    mobile: false,
    parentId: "projects",
  },
  {
    id: "follow-ups",
    path: "/follow-ups",
    label: "Nachfragen",
    icon: CalendarClock,
    group: "workspace",
    mobile: false,
    parentId: "inbox",
  },

  // Fussbereich.
  {
    id: "settings",
    path: "/settings",
    label: "Einstellungen",
    icon: Settings,
    group: "footer",
    mobile: false,
    hotkey: "s", // alter Chord `g s` (hotkeys.ts) bleibt erhalten
  },
  {
    id: "admin",
    path: "/admin",
    label: "Admin",
    icon: Shield,
    group: "footer",
    mobile: false,
    adminOnly: true,
  },
];

/** Hauptnavigation (Sidebar-Rail, oberer Bereich), in Katalogreihenfolge. */
export function primaryNav(): NavTarget[] {
  // UX-Vorschlag P1: „Mein Tag" ist Startseite, das Dashboard verlaesst die
  // Hauptnavigation (bleibt per /dashboard, Befehlspalette und `g u` da).
  // Reaktiv ueber uxEnabled() — bei classic + Flag „vorschau" unveraendert.
  const hideDashboard = uxEnabled("ux_web_p1");
  return NAV_TARGETS.filter(
    (t) => t.group === "primary" && !(hideDashboard && t.id === "dashboard"),
  );
}

/** Ziel von `/` (und `/home`): mit P1 „Mein Tag", sonst wie bisher das Dashboard. */
export function startPath(): string {
  return uxEnabled("ux_web_p1") ? "/today" : "/dashboard";
}

/** Mobile Bottom-Bar — Projektion von primaryNav(), keine eigene Liste. Max. 5. */
export function mobileNav(): NavTarget[] {
  return primaryNav()
    .filter((t) => t.mobile)
    .slice(0, 5);
}

/** Fussbereich; Admin-Ziele nur wenn `isAdmin`. */
export function footerNav(isAdmin: boolean): NavTarget[] {
  return NAV_TARGETS.filter(
    (t) => t.group === "footer" && (!t.adminOnly || isAdmin),
  );
}

/** Alle Ziele, wie sie die Befehlspalette anbieten soll. */
export function paletteTargets(): readonly NavTarget[] {
  return NAV_TARGETS;
}

/** Nur Ziele mit einem g-Chord-Hotkey. */
export function hotkeyTargets(): NavTarget[] {
  return NAV_TARGETS.filter((t) => t.hotkey !== undefined);
}

/**
 * Bildet JEDEN g-Chord-Buchstaben — Haupt-Hotkey UND `hotkeyAliases` — auf
 * sein Ziel ab. Ersetzt `G_CHORDS` in hotkeys.ts (Paket 2): dort ist eine
 * Map die passende Form, keine Liste.
 */
export function hotkeyMap(): Record<string, NavTarget> {
  const map: Record<string, NavTarget> = {};
  for (const t of NAV_TARGETS) {
    if (t.hotkey !== undefined) map[t.hotkey] = t;
    for (const alias of t.hotkeyAliases ?? []) map[alias] = t;
  }
  return map;
}

/** Findet ein Ziel über seinen exakten Routen-Pfad. */
export function findByPath(path: string): NavTarget | undefined {
  return NAV_TARGETS.find((t) => t.path === path);
}

/**
 * Der verbindliche Seitentitel für eine Route, die ein Navigationsziel ist.
 *
 * WARUM das nötig ist: nach dem Umbau auf `usePageHeader` trug jede Route den
 * Titel, den sie vorher schon hatte — und der wich bei SIEBEN von zehn Zielen
 * vom Sidebar-Label ab. Man klickte „Inbox" und landete auf einer Seite mit
 * der Überschrift „Posteingang". Das ist genau dieselbe Drift, die zwischen
 * Sidebar und mobiler Leiste schon einmal bestand, nur eine Ebene weiter.
 *
 * Routen rufen deshalb `requireLabel("/inbox")` statt eine Zeichenkette zu
 * wiederholen. Fällt der Pfad aus dem Katalog, erscheint der Pfad selbst als
 * Titel — sichtbar falsch, aber ohne Absturz; der Test in
 * `lib/__tests__/navigation.test.ts` schlägt dann fehl.
 *
 * Feinere Formulierungen gehören in `subtitle`, nicht in einen abweichenden
 * Titel: „Einstellungen" + „Steuere wie und worüber dich juuplan
 * benachrichtigt." statt „Persönliche Einstellungen".
 */
export function requireLabel(path: string): string {
  return findByPath(path)?.label ?? path;
}

/**
 * Aktiv-Erkennung: `location === target.path` oder `location` beginnt mit
 * `target.path + "/"`. Deckt Unterpfade ab (z. B. `/projects/5` für
 * `/projects`), ohne dass `/my-tasks` durch `/my-tasks-foo` aktiv wird und
 * ohne dass `/` (als Pfad eines Ziels) alles aktiviert.
 */
export function isActivePath(target: NavTarget, currentLocation: string): boolean {
  return (
    currentLocation === target.path ||
    currentLocation.startsWith(`${target.path}/`)
  );
}
